import { useState, type FormEvent } from 'react';
import { useRouter } from '@tanstack/react-router';
import { ApiRequestError } from '../../shared/api';
import { useChangePassword, useSession } from '../../shared/session';

/** §15.3 ТЗ. Смена начального пароля обязательна: до неё разделы недоступны. */
export function PasswordPage() {
  const session = useSession();
  const router = useRouter();
  const change = useChangePassword();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const forced = session.employee?.mustChangePassword ?? false;
  // Менеджер паролей подставил сгенерированный пароль в оба поля: сервер
  // ответит «текущий пароль неверен», и человек будет искать ошибку в себе
  const clobbered = currentPassword !== '' && currentPassword === newPassword;

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (clobbered) return;
    change.mutate({ currentPassword, newPassword }, { onSuccess: () => router.history.push('/') });
  };

  return (
    <main className="centered">
      <form className="card" onSubmit={onSubmit}>
        <h1>Смена пароля</h1>
        {forced && <p className="muted">Начальный пароль нужно сменить до начала работы.</p>}

        {/*
          Поле с учётной записью — не украшение: без него менеджер паролей не
          понимает, для кого форма, и предлагает «новый надёжный пароль» сразу в
          оба поля. Спрятано оно стилями, а не атрибутом `hidden`: поле с
          `hidden` браузер при разборе формы пропускает, и смысл исчезает.
        */}
        <input
          type="text"
          name="username"
          autoComplete="username"
          className="visually-hidden"
          value={session.employee?.email ?? ''}
          readOnly
          tabIndex={-1}
          aria-hidden="true"
        />

        <SecretField
          id="password-old"
          name="current-password"
          autoComplete="current-password"
          label="Прежний пароль"
          hint={forced ? 'Тот, который вам выдал администратор.' : 'Тот, с которым вы вошли.'}
          value={currentPassword}
          onChange={setCurrentPassword}
        />

        <SecretField
          id="password-chosen"
          name="new-password"
          autoComplete="new-password"
          label="Придуманный новый пароль"
          hint="Не короче 12 символов."
          minLength={12}
          value={newPassword}
          onChange={setNewPassword}
        />

        {clobbered && (
          <p className="error" role="alert">
            В обоих полях один и тот же пароль — похоже, его подставил менеджер паролей браузера. В
            верхнем поле нужен прежний пароль; нажмите «показать», чтобы проверить.
          </p>
        )}

        {!clobbered && change.isError && (
          <p className="error" role="alert">
            {change.error instanceof ApiRequestError
              ? change.error.payload.message
              : 'Не удалось сменить пароль.'}
          </p>
        )}

        <button type="submit" disabled={change.isPending || clobbered}>
          Сохранить
        </button>
      </form>
    </main>
  );
}

/**
 * Поле пароля с показом значения.
 *
 * Показ здесь нужнее обычного: пароль выдаёт администратор, его переписывают
 * руками или вставляют из переписки, а менеджер паролей норовит подставить
 * своё. Не видя символов, человек не отличит опечатку от подстановки.
 */
function SecretField(props: {
  id: string;
  name: string;
  autoComplete: string;
  label: string;
  hint: string;
  value: string;
  minLength?: number;
  onChange: (value: string) => void;
}) {
  const [visible, setVisible] = useState(false);

  return (
    <>
      <label htmlFor={props.id}>{props.label}</label>
      <div className="secret-field">
        <input
          id={props.id}
          name={props.name}
          type={visible ? 'text' : 'password'}
          autoComplete={props.autoComplete}
          required
          minLength={props.minLength}
          value={props.value}
          onChange={(event) => props.onChange(event.target.value)}
        />
        <button
          type="button"
          className="secret-field__toggle"
          onClick={() => setVisible((shown) => !shown)}
          aria-pressed={visible}
          aria-controls={props.id}
          title={visible ? 'Скрыть пароль' : 'Показать пароль'}
        >
          {visible ? 'скрыть' : 'показать'}
        </button>
      </div>
      <p className="muted">{props.hint}</p>
    </>
  );
}
